// @hamecho/content-schema is imported as a relative path to local content-schema package
import { StockNodeSchema } from "@hamecho/content-schema";
import { $applyNodeReplacement, DecoratorNode, type LexicalNode, type NodeKey, type SerializedLexicalNode, Spread } from "lexical";
import { JSX } from "react";
import { NODE_TYPE_ENTITY } from "@/constants/lexical";

export type SerializedStockNode = Spread<
  {
    type: typeof NODE_TYPE_ENTITY.STOCK;
    version: 1;
    key: string;
    symbol: string;
    sentiment: "bullish" | "bearish" | "neutral";
    confidence: number | null;
  },
  SerializedLexicalNode
>;

/**
 * StockNode - 股票实体节点（原子节点）
 *
 * 特性：
 * - DecoratorNode（装饰节点）：React 组件渲染
 * - 原子节点：不可分割，整体删除
 * - 支持看涨/看跌情绪标记
 * - 支持信心评分（0-100）
 *
 * Per architecture: Node holds value fields directly. Content Schema is SSOT via Adapter.
 */
export class StockNode extends DecoratorNode<JSX.Element> {
  __symbol: string;
  __sentiment: "bullish" | "bearish" | "neutral";
  __confidence: number | null;

  static getType(): string {
    return NODE_TYPE_ENTITY.STOCK;
  }

  static clone(node: StockNode): StockNode {
    return new StockNode(node.__symbol, node.__sentiment, node.__confidence, node.__key);
  }

  constructor(symbol: string, sentiment: "bullish" | "bearish" | "neutral" = "neutral", confidence: number | null = null, key?: NodeKey) {
    super(key);
    this.__symbol = symbol;
    this.__sentiment = sentiment;
    this.__confidence = confidence;
  }

  // ========== 关键：原子节点行为声明 ==========

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  // ========== DOM 操作 ==========

  createDOM(): HTMLElement {
    // 只返回空容器，样式在 decorate() 中
    return document.createElement("span");
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    const sentimentColor = {
      bullish: "bg-green-50 text-green-700 hover:bg-green-100",
      bearish: "bg-red-50 text-red-700 hover:bg-red-100",
      neutral: "bg-gray-50 text-gray-700 hover:bg-gray-100",
    } as const;

    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors cursor-pointer select-none ${sentimentColor[this.__sentiment]}`}
      >
        ${this.__symbol}
      </span>
    );
  }

  // ========== 序列化 ==========

  static importJSON(serializedNode: SerializedStockNode): StockNode {
    // Validate with schema
    StockNodeSchema.parse({
      type: NODE_TYPE_ENTITY.STOCK,
      version: 1,
      key: serializedNode.key,
      symbol: serializedNode.symbol,
      sentiment: serializedNode.sentiment,
      confidence: serializedNode.confidence,
    });

    const node = $createStockNode(serializedNode.symbol, serializedNode.sentiment, serializedNode.confidence);
    return node;
  }

  exportJSON(): SerializedStockNode {
    const json = {
      ...super.exportJSON(),
      type: NODE_TYPE_ENTITY.STOCK,
      version: 1 as const,
      key: this.__key,
      symbol: this.__symbol,
      sentiment: this.__sentiment,
      confidence: this.__confidence,
    };

    StockNodeSchema.parse(json);
    return json;
  }

  // ========== Getter/Setter ==========

  getSymbol(): string {
    return this.__symbol;
  }

  setSymbol(symbol: string): void {
    const writable = this.getWritable();
    writable.__symbol = symbol;
  }

  getSentiment(): "bullish" | "bearish" | "neutral" {
    return this.__sentiment;
  }

  setSentiment(sentiment: "bullish" | "bearish" | "neutral"): void {
    const writable = this.getWritable();
    writable.__sentiment = sentiment;
  }

  getConfidence(): number | null {
    return this.__confidence;
  }

  setConfidence(confidence: number | null): void {
    const writable = this.getWritable();
    writable.__confidence = confidence;
  }

  // ========== 其他 ==========

  getTextContent(): string {
    return `$${this.__symbol}`;
  }

  isEqualTo(other: LexicalNode): boolean {
    if (!(other instanceof StockNode)) {
      return false;
    }
    return this.__symbol === other.__symbol && this.__sentiment === other.__sentiment && this.__confidence === other.__confidence;
  }
}

/**
 * 创建 StockNode
 */
export function $createStockNode(
  symbol: string,
  sentiment: "bullish" | "bearish" | "neutral" = "neutral",
  confidence: number | null = null,
): StockNode {
  const stockNode = new StockNode(symbol, sentiment, confidence);
  return $applyNodeReplacement(stockNode);
}
